import { expiryInfo, nItems } from "../lib/format";
import { TYPES } from "../lib/itemTypes";
import { useVault } from "../state/store";
import { Badge, Button, Eyebrow, IconTile } from "../ui/ds";
import { usePerspective, useUI } from "./ui";

/** All clients: one card per client vault. */
export function ClientsView() {
  const { data } = useVault();
  const p = usePerspective();
  const { openVault, setModal } = useUI();
  const clients = p.vaults.filter((v) => v.kind === "client");
  return (
    <div className="scroll">
      <div className="page-head page-head-row">
        <div style={{ maxWidth: 560 }}>
          <Eyebrow size="sm">Clients</Eyebrow>
          <h1 className="page-h1">Client vaults</h1>
          <div className="page-meta">One vault per client. Share it, request credentials into it, revoke it when the work ends.</div>
        </div>
        <Button icon="plus" size="sm" onClick={() => setModal({ kind: "client" })}>
          New client vault
        </Button>
      </div>
      <div className="page-body">
        <div className="client-grid">
          {clients.map((v) => {
            const members = data.members.filter((m) => m.vaultId === v.id);
            const invites = data.invites.filter((i) => i.vaultId === v.id);
            const n = members.length + invites.length;
            const notReady = invites.length;
            const w = p.waiting(v);
            const ve = p
              .liveItems(v)
              .map((i) => expiryInfo(i))
              .filter((x) => x && x.status !== "ok");
            return (
              <button key={v.id} type="button" className="card client-card hover-lift" onClick={() => openVault(v.id)}>
                <div className="accent-rule" />
                <div>
                  <div className="card-title">{v.name}</div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{v.contactName || v.contactEmail || "No contact yet"}</div>
                </div>
                <div className="mono" style={{ fontSize: 12 }}>
                  {nItems(p.liveItems(v).length)} · {n} {n === 1 ? "person" : "people"}
                </div>
                <div className="badges" style={{ marginTop: 0 }}>
                  <Badge tone={n === 0 ? "neutral" : notReady ? "warning" : "success"}>
                    {n === 0 ? "Not shared yet" : notReady ? `${notReady} not on 2FA yet` : "Everyone on 2FA"}
                  </Badge>
                  {w ? (
                    <Badge tone="teal" dot>
                      {w} waiting
                    </Badge>
                  ) : null}
                  {ve.length ? (
                    <Badge tone={ve.some((x) => x!.status === "expired") ? "danger" : "warning"} dot>
                      {ve.length} expiring
                    </Badge>
                  ) : null}
                </div>
              </button>
            );
          })}
          {!clients.length ? (
            <div className="card card-empty">No client vaults yet. Create one per client, then invite their contact.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Everything inside its warning window, soonest first. */
export function ExpiringView() {
  const { data } = useVault();
  const p = usePerspective();
  const { openVault } = useUI();
  const rows = p.expiring;
  const nExpired = rows.filter((x) => x.ex.status === "expired").length;
  return (
    <div className="scroll">
      <div className="page-head">
        <Eyebrow size="sm">Expiry</Eyebrow>
        <h1 className="page-h1">Expiring soon</h1>
        <div className="page-meta">
          {rows.length
            ? `${nExpired ? `${nExpired} expired · ` : ""}${rows.length - nExpired} inside their warning window. Rotate them and update the item.`
            : "Nothing needs rotating right now."}
        </div>
      </div>
      <div className="page-body">
        <div className="card list-card">
          {rows.map(({ i, ex }) => {
            const vault = data.vaults.find((v) => v.id === i.vaultId);
            const cat = data.categories.find((c) => c.id === i.catId);
            return (
              <button key={i.id} type="button" className="list-card-row" onClick={() => openVault(i.vaultId, { cat: "all", itemId: i.id, pane: "detail" })}>
                <IconTile icon={TYPES[i.type]?.icon ?? "file"} />
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <div className="member-name">{i.title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {vault?.name}
                    {cat ? ` · ${cat.name}` : ""}
                  </div>
                </div>
                <span className="mono" style={{ fontSize: 13 }}>
                  {ex.date}
                </span>
                <Badge tone={ex.tone} dot>
                  {ex.text}
                </Badge>
              </button>
            );
          })}
          {!rows.length ? (
            <div className="card-empty" style={{ padding: "32px 24px" }}>
              Nothing is inside its warning window. You'll see items here before they expire.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
